import toast from 'react-hot-toast';

import Request from '@/services/util/request-service';
import {
  ActivityProgressCompletionData,
  CourseId,
  CourseProgressCompletionData,
  CourseProgressEntity,
  QuizAttempt,
  RawUserCourseProgressData,
  SubunitId,
  UnitId,
  CourseData,
  UnitData,
  SubunitData,
} from 'course-types';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';

// -----------------------------------------------------------------------------
// Constants & Types
// -----------------------------------------------------------------------------
/**
 * Activity types that are tracked for progress. Centralizing these avoids
 * scattered string literals and keeps type-safety.
 */
export const ACTIVITY_TYPES = ['quiz', 'video', 'document', 'assignment'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

const ProgressUtilityService = {
  async getStudentProgress(
    email: string,
    courseId: string,
  ): Promise<{ data: Array<RawUserCourseProgressData> }> {
    const body = { email: email, courseId: courseId };
    const progress = await (await Request.post('progress', body))?.json();
    return progress;
  },

  /**
   * Self-scoped: returns the logged-in user's own attempts for a quiz.
   * Backed by `POST progress/attempts`, which resolves the user from the
   * auth cookie rather than requiring admin/group-visibility permissions.
   */
  async getMyQuizAttempts(quizId: string): Promise<Array<QuizAttempt>> {
    const response = await (await Request.post('progress/attempts', { quizId }))?.json();
    return response?.attempts ?? [];
  },

  async updateUserCourseProgressDatabase(
    courseId: CourseId,
    data: ActivityProgressCompletionData,
    unitId?: UnitId,
    subunitId?: SubunitId,
  ): Promise<void> {
    if (!unitId && !subunitId) {
      toast.error('Failed to save user progress');
      return;
    }
    const bodyData: any = {
      courseId: courseId,
      completion_data: data,
    };

    if (subunitId && subunitId != ('' as SubunitId)) {
      bodyData.subunitId = subunitId;
    } else if (unitId) {
      bodyData.unitId = unitId;
    }

    await Request.post(`user/progress/activities`, bodyData);
    return;
  },

  validateCourseDataExists(
    data: Record<CourseId, CourseProgressEntity>,
    courseId: CourseId,
    unitId?: UnitId,
    subunitId?: SubunitId,
  ): Record<CourseId, CourseProgressEntity> {
    if (!data[courseId]) {
      data[courseId] = {
        completion_data: {
          subunits: {},
          units: {},
        },
      };
    }

    // Ensure 'units' is an object.
    if (
      typeof data[courseId].completion_data.units !== 'object' ||
      data[courseId].completion_data.units === null
    ) {
      data[courseId].completion_data.units = {};
    }

    if (unitId && !data[courseId].completion_data.units[unitId]) {
      data[courseId].completion_data.units[unitId] = {
        video: {},
        document: {},
        quiz: {},
        assignment: {},
        aurora: [],
      };
    } else if (unitId) {
      const unitProgress = data[courseId].completion_data.units[unitId] as any;
      unitProgress.video ??= {};
      unitProgress.document ??= {};
      unitProgress.quiz ??= {};
      unitProgress.assignment ??= {};
      unitProgress.aurora ??= [];
    }

    if (subunitId && !data[courseId].completion_data.subunits[subunitId]) {
      data[courseId].completion_data.subunits[subunitId] = {
        video: {},
        document: {},
        quiz: {},
        assignment: {},
        aurora: [],
      };
    } else if (subunitId) {
      const subunitProgress = data[courseId].completion_data.subunits[subunitId] as any;
      subunitProgress.video ??= {};
      subunitProgress.document ??= {};
      subunitProgress.quiz ??= {};
      subunitProgress.assignment ??= {};
      subunitProgress.aurora ??= [];
    }

    return data;
  },

  /**
   * Sync cached progress with the latest course structure.
   * Adds new activities and prunes removed ones.
   *
   * @param courseData The course data to sync with.
   * @returns A promise that resolves when the sync is complete.
   */
  async cleanupProgressData(courseData: CourseData): Promise<void> {
    const progress = UserProgressStorageService.getUserCourseProgressCookie();
    if (!progress || !progress[courseData.id]) return;

    // -------------------------------------------------------------------
    // Defensive guard – if the backend failed to provide any course
    // structure (e.g. network error returns empty units/subunits), bail out
    // early to prevent accidental deletion of the user's cached progress.
    // -------------------------------------------------------------------
    const hasUnits = courseData.units && Object.keys(courseData.units).length > 0;
    if (!hasUnits) {
      return;
    }

    const courseProgress = progress[courseData.id];
    let hasChanges = false;

    // Utility: type-guard leveraging the central `ACTIVITY_TYPES` constant.
    const isValidActivityType = (type: string): type is ActivityType =>
      (ACTIVITY_TYPES as readonly string[]).includes(type);

    const processActivityType = (
      activities: Array<{ id: string }> | undefined,
      progressSection: ActivityProgressCompletionData,
      activityType: keyof Omit<ActivityProgressCompletionData, 'aurora' | 'completion_status'>,
    ): Set<string> => {
      // Initialize activity type section if it doesn't exist
      if (!progressSection[activityType] || typeof progressSection[activityType] === 'undefined') {
        switch (activityType) {
          case 'quiz':
            progressSection.quiz = {};
            break;
          case 'video':
            progressSection.video = {};
            break;
          case 'document':
            progressSection.document = {};
            break;
          case 'assignment':
            progressSection.assignment = {};
            break;
        }
        hasChanges = true;
      }

      // Get set of existing activity IDs in progress
      const section = progressSection[activityType];
      if (section && typeof section === 'object' && !Array.isArray(section)) {
        const existingIds = new Set(Object.keys(section));

        // Add missing activities and remove found ones from existingIds
        activities?.forEach((activity) => {
          if (!section[activity.id]) {
            section[activity.id] = {};
            hasChanges = true;
          }
          existingIds.delete(activity.id);
        });

        return existingIds;
      }

      return new Set<string>();
    };

    // Process unit activities
    const existingUnitIds = new Set(Object.keys(courseProgress.completion_data.units));
    const existingSubunitIds = new Set(Object.keys(courseProgress.completion_data.subunits));

    Object.entries(courseData.units || {}).forEach(([unitId, unit]) => {
      const typedUnitId = unitId as UnitId;
      // Initialize unit section if it doesn't exist
      if (!courseProgress.completion_data.units[typedUnitId]) {
        courseProgress.completion_data.units[typedUnitId] = {
          video: {},
          document: {},
          quiz: {},
          assignment: {},
          aurora: [],
        };
        hasChanges = true;
      }

      const unitProgress = courseProgress.completion_data.units[typedUnitId];

      // Process each activity type
      ['quiz', 'video', 'document', 'assignment'].forEach((type) => {
        if (isValidActivityType(type)) {
          const remainingIds = processActivityType(
            unit.activities?.filter((a) => a.type === type),
            unitProgress,
            type,
          );

          // Remove non-existent activities
          remainingIds.forEach((id) => {
            if (unitProgress[type][id]) {
              delete unitProgress[type][id];
              hasChanges = true;
            }
          });
        }
      });

      existingUnitIds.delete(typedUnitId);

      // Process subunit activities
      Object.entries(unit.subunits || {}).forEach(([subunitId, subunit]) => {
        const typedSubunitId = subunitId as SubunitId;
        // Initialize subunit section if it doesn't exist
        if (!courseProgress.completion_data.subunits[typedSubunitId]) {
          courseProgress.completion_data.subunits[typedSubunitId] = {
            video: {},
            document: {},
            quiz: {},
            assignment: {},
            aurora: [],
          };
          hasChanges = true;
        }

        const subunitProgress = courseProgress.completion_data.subunits[typedSubunitId];

        // Process each activity type
        ['quiz', 'video', 'document', 'assignment'].forEach((type) => {
          if (isValidActivityType(type)) {
            const remainingIds = processActivityType(
              subunit.activities?.filter((a) => a.type === type),
              subunitProgress,
              type,
            );

            // Remove non-existent activities
            remainingIds.forEach((id) => {
              if (subunitProgress[type][id]) {
                delete subunitProgress[type][id];
                hasChanges = true;
              }
            });
          }
        });

        existingSubunitIds.delete(typedSubunitId);
      });
    });

    // Remove non-existent units
    existingUnitIds.forEach((id) => {
      const typedId = id as UnitId;
      delete courseProgress.completion_data.units[typedId];
      hasChanges = true;
    });

    // Remove non-existent subunits
    existingSubunitIds.forEach((id) => {
      const typedId = id as SubunitId;
      delete courseProgress.completion_data.subunits[typedId];
      hasChanges = true;
    });

    // Update progress cookie if changes were made
    if (hasChanges) {
      UserProgressStorageService.setUserCourseProgressCookie(progress);

      // Server-side progress is now stored per activity, and aggregate
      // reads ignore activities that no longer exist in the course
      // structure. Local cleanup is sufficient here.
    }
  },

  calculateCourseProgress(id: CourseId): number {
    // If no progress exists for this course yet, treat as 0% (uninitialized),
    // not 100%. We'll only report 100% for truly zero-activity courses once
    // a course progress entry exists.
    const cookie = UserProgressStorageService.getUserCourseProgressCookie();
    if (!cookie || !cookie[id]) {
      return 0;
    }

    // If the entry exists but both units and subunits are empty, treat as
    // uninitialized (0%). We'll compute 100% only when total trackable
    // activities is actually 0 after structure sync.
    const entry = cookie[id];
    const unitsEmpty =
      !entry.completion_data?.units || Object.keys(entry.completion_data.units).length === 0;
    const subunitsEmpty =
      !entry.completion_data?.subunits || Object.keys(entry.completion_data.subunits).length === 0;
    if (unitsEmpty && subunitsEmpty) {
      return 0;
    }

    const { total, completed } = this.getCourseCompletionProgress(id);
    return total === 0 ? 100 : Math.round((completed / total) * 100);
  },

  getCourseCompletionProgress(id: CourseId): { total: number; completed: number } {
    const progress = UserProgressStorageService.getUserCourseProgressCookie();
    if (!progress || !progress[id]) {
      return { total: 0, completed: 0 };
    }

    const data: CourseProgressCompletionData = progress[id].completion_data;

    let total = 0;
    let completed = 0;

    Object.values(data).forEach((section) => {
      Object.values(section).forEach((unit) => {
        // Quizzes
        for (const quiz of Object.values(unit.quiz ?? {})) {
          total++;
          if ((quiz as { attempts?: number }).attempts! > 0) completed++;
        }

        // Documents, Videos, & Assignments – completed only when `submissionDate` exists.
        (['document', 'video', 'assignment'] as ActivityType[]).forEach((type) => {
          const collection = unit[type] ?? {};
          for (const item of Object.values(collection)) {
            total++;
            if ((item as { submissionDate?: Date }).submissionDate) completed++;
          }
        });
      });
    });

    return { total, completed };
  },

  /**
   * Count the total number of trackable activities (quiz, video, document, assignment)
   * within a given unit, including its subunits.
   */
  getUnitTrackableTotal(unit: UnitData): number {
    const isTrackable = (t: string) =>
      t === 'quiz' || t === 'video' || t === 'document' || t === 'assignment';

    const unitLevel = (unit.activities || []).filter((a) => isTrackable(a.type)).length;
    const subunitsTotal = Object.values(unit.subunits || {}).reduce((sum, sub) => {
      const count = (sub.activities || []).filter((a) => isTrackable(a.type)).length;
      return sum + count;
    }, 0);

    return unitLevel + subunitsTotal;
  },

  /**
   * Count how many activities are completed within a unit (and its subunits),
   * using the same completion criteria as course-level progress:
   * - quiz: attempts > 0
   * - document/video/assignment: submissionDate present
   */
  getUnitCompletedActivities(courseId: CourseId, unitId: UnitId, unit: UnitData): number {
    const progress = UserProgressStorageService.getUserCourseProgressCookie() || {};

    const data: CourseProgressCompletionData | undefined = progress[courseId]?.completion_data;
    const unitProgress: ActivityProgressCompletionData | undefined = data?.units?.[unitId];

    let completed = 0;

    // Unit-level activities
    (unit.activities || []).forEach((a) => {
      if (a.type === 'quiz') {
        const q = unitProgress?.quiz?.[a.id];
        if (q && (q.attempts ?? 0) > 0) completed++;
      } else if (a.type === 'video') {
        const v = unitProgress?.video?.[a.id] as { submissionDate?: Date } | undefined;
        if (v?.submissionDate) completed++;
      } else if (a.type === 'document') {
        const d = unitProgress?.document?.[a.id] as { submissionDate?: Date } | undefined;
        if (d?.submissionDate) completed++;
      } else if (a.type === 'assignment') {
        const aProgress = unitProgress?.assignment?.[a.id] as { submissionDate?: Date } | undefined;
        if (aProgress?.submissionDate) completed++;
      }
    });

    // Subunit activities
    Object.entries(unit.subunits || {}).forEach(([subunitId, sub]) => {
      const subProg: ActivityProgressCompletionData | undefined =
        data?.subunits?.[subunitId as SubunitId];

      (sub.activities || []).forEach((a) => {
        if (a.type === 'quiz') {
          const q = subProg?.quiz?.[a.id];
          if (q && (q.attempts ?? 0) > 0) completed++;
        } else if (a.type === 'video') {
          const v = subProg?.video?.[a.id] as { submissionDate?: Date } | undefined;
          if (v?.submissionDate) completed++;
        } else if (a.type === 'document') {
          const d = subProg?.document?.[a.id] as { submissionDate?: Date } | undefined;
          if (d?.submissionDate) completed++;
        } else if (a.type === 'assignment') {
          const aProgress = subProg?.assignment?.[a.id] as { submissionDate?: Date } | undefined;
          if (aProgress?.submissionDate) completed++;
        }
      });
    });

    return completed;
  },

  /**
   * Count the total number of trackable activities for a specific subunit.
   */
  getSubunitTrackableTotal(subunit: SubunitData): number {
    const isTrackable = (t: string) =>
      t === 'quiz' || t === 'video' || t === 'document' || t === 'assignment';
    return (subunit.activities || []).filter((a) => isTrackable(a.type)).length;
  },

  /**
   * Count completed activities for a specific subunit, using the same criteria as course progress.
   */
  getSubunitCompletedActivities(
    courseId: CourseId,
    subunitId: SubunitId,
    subunit: SubunitData,
  ): number {
    const progress = UserProgressStorageService.getUserCourseProgressCookie() || {};

    const data: CourseProgressCompletionData | undefined = progress[courseId]?.completion_data;
    const subProg: ActivityProgressCompletionData | undefined = data?.subunits?.[subunitId];

    let completed = 0;

    (subunit.activities || []).forEach((a) => {
      if (a.type === 'quiz') {
        const q = subProg?.quiz?.[a.id];
        if (q && (q.attempts ?? 0) > 0) completed++;
      } else if (a.type === 'video') {
        const v = subProg?.video?.[a.id] as { submissionDate?: Date } | undefined;
        if (v?.submissionDate) completed++;
      } else if (a.type === 'document') {
        const d = subProg?.document?.[a.id] as { submissionDate?: Date } | undefined;
        if (d?.submissionDate) completed++;
      } else if (a.type === 'assignment') {
        const aProgress = subProg?.assignment?.[a.id] as { submissionDate?: Date } | undefined;
        if (aProgress?.submissionDate) completed++;
      }
    });

    return completed;
  },
};

export default ProgressUtilityService;
